/* ==========================================================================
   Semáforo por cantidad de piezas — Maje MX
   --------------------------------------------------------------------------
   Lee StoreAvailability.quantityAvailable de la Storefront API y ajusta el
   estado de cada tienda en el panel:
     > umbral piezas  -> verde    "Disponible"
     1..umbral        -> amarillo "Disponible — Pocas piezas disponibles"
     0                -> rojo     "No disponible"

   La configuración (token, endpoint, umbral, textos) viene de los data-pa-*
   de <pickup-availability-preview> en sections/pickup-availability.liquid.

   Si no hay token o la API falla, no se toca nada: el panel queda con el
   estado verde/rojo que ya pintó Liquid.
   ========================================================================== */

const paStockCache = new Map();

const PA_STOCK_QUERY = `
  query PickupStock($id: ID!) {
    node(id: $id) {
      ... on ProductVariant {
        storeAvailability(first: 50) {
          nodes {
            quantityAvailable
            location { name }
          }
        }
      }
    }
  }
`;

function paNormalize(value) {
  return String(value || '').trim().toLowerCase();
}

function paFetchStockLevels(config, variantId) {
  const key = String(variantId);
  if (paStockCache.has(key)) return paStockCache.get(key);

  const request = fetch(config.paEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': config.paToken,
    },
    body: JSON.stringify({
      query: PA_STOCK_QUERY,
      variables: { id: `gid://shopify/ProductVariant/${key}` },
    }),
  })
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    })
    .then((json) => {
      if (json.errors && json.errors.length) {
        throw new Error(json.errors.map((error) => error.message).join('; '));
      }
      const node = json.data && json.data.node;
      const nodes = node && node.storeAvailability && node.storeAvailability.nodes;
      if (!Array.isArray(nodes)) throw new Error('La respuesta no trae storeAvailability');

      const levels = new Map();
      nodes.forEach((entry) => {
        if (entry && entry.location && typeof entry.quantityAvailable === 'number') {
          levels.set(paNormalize(entry.location.name), entry.quantityAvailable);
        }
      });
      return levels;
    });

  paStockCache.set(key, request);
  request.catch(() => paStockCache.delete(key));
  return request;
}

function paSetItemState(item, state, labels) {
  item.dataset.paState = state;
  item.classList.toggle('pa-item--muted', state === 'red');

  const status = item.querySelector('[data-pa-item-status]');
  if (status) {
    status.classList.remove('pa-status--green', 'pa-status--amber', 'pa-status--red');
    status.classList.add(`pa-status--${state}`);
  }

  const label = item.querySelector('[data-pa-item-label]');
  if (label && labels[state]) label.textContent = labels[state];
}

function paApplyStockLevels(preview, drawer, levels) {
  const config = preview.dataset;
  const threshold = parseInt(config.paLowThreshold, 10) || 1;
  const labels = {
    green: config.paLabelGreen,
    amber: config.paLabelAmber,
    red: config.paLabelRed,
  };

  const availableGroup = drawer.querySelector('[data-pa-group="available"]');
  const unavailableGroup = drawer.querySelector('[data-pa-group="unavailable"]');
  if (!availableGroup || !unavailableGroup) return;

  const availableList = availableGroup.querySelector('[data-pa-list]');
  const unavailableList = unavailableGroup.querySelector('[data-pa-list]');
  if (!availableList || !unavailableList) return;

  const items = Array.from(drawer.querySelectorAll('[data-pa-item]'));
  const green = [];
  const amber = [];
  const red = [];

  items.forEach((item) => {
    const key = paNormalize(item.dataset.locationName);
    let state = item.dataset.paState || 'red';

    // Solo se sobrescribe el estado de Liquid cuando la API trae la cantidad
    // de esa tienda. Las tiendas de la lista maestra no vienen en la API y
    // siguen en rojo.
    if (levels.has(key)) {
      const quantity = levels.get(key);
      if (quantity <= 0) state = 'red';
      else if (quantity <= threshold) state = 'amber';
      else state = 'green';
    }

    paSetItemState(item, state, labels);
    if (state === 'green') green.push(item);
    else if (state === 'amber') amber.push(item);
    else red.push(item);
  });

  // Verde primero, luego amarillo, respetando el orden original en cada grupo
  green.concat(amber).forEach((item) => availableList.appendChild(item));
  red.forEach((item) => unavailableList.appendChild(item));

  const readyCount = green.length + amber.length;

  availableGroup.hidden = readyCount === 0;
  const availableCount = availableGroup.querySelector('[data-pa-count]');
  if (availableCount) availableCount.textContent = readyCount;

  paUpdatePickupBanner(drawer, readyCount);

  unavailableGroup.hidden = red.length === 0;
  unavailableGroup.open = readyCount === 0;
  const unavailableCount = unavailableGroup.querySelector('[data-pa-count]');
  if (unavailableCount) unavailableCount.textContent = red.length;

  // Resumen en la ficha de producto: el mejor estado entre todas las tiendas
  const previewStatus = preview.querySelector('[data-pa-preview-status]');
  const previewText = preview.querySelector('[data-pa-preview-text]');
  let previewState = 'red';
  if (green.length > 0) previewState = 'green';
  else if (amber.length > 0) previewState = 'amber';
  const message = labels[previewState];

  if (previewStatus) {
    previewStatus.classList.remove('pa-status--green', 'pa-status--amber', 'pa-status--red');
    previewStatus.classList.add(`pa-status--${previewState}`);
  }
  if (previewText && message) previewText.textContent = message;
}

// Aviso "Compra en linea y recoge en tienda" y boton de compra: visibles solo
// cuando hay al menos una tienda disponible. El contador se ajusta si la API
// cambia el numero de tiendas con piezas.
function paUpdatePickupBanner(drawer, readyCount) {
  const banner = drawer.querySelector('[data-pa-pickup]');
  const cta = drawer.querySelector('[data-pa-pickup-cta]');
  if (banner) banner.hidden = readyCount === 0;
  if (cta) cta.hidden = readyCount === 0;

  const count = drawer.querySelector('[data-pa-pickup-count]');
  if (count) {
    const word = readyCount === 1 ? count.dataset.wordOne : count.dataset.wordOther;
    count.textContent = `${readyCount} ${word}`;
  }
}

// "Anadir a la bolsa" dentro del panel.
// 1) Si el formulario de producto del theme ya tiene esta variante y su boton
//    esta habilitado, se usa ese boton: el theme abre su propio carrito.
// 2) Si no (por ejemplo, el cliente aun no eligio talla en el selector), se
//    anade con /cart/add.js y se lleva al cliente al carrito.
function paFindThemeSubmit(variantId) {
  const forms = document.querySelectorAll('form[action*="/cart/add"]');
  for (const form of forms) {
    if (form.closest('pickup-availability-drawer')) continue;
    const idInput = form.querySelector('[name="id"]');
    if (!idInput || String(idInput.value) !== String(variantId)) continue;
    const submit =
      form.querySelector('[type="submit"][name="add"]') ||
      document.querySelector(`[type="submit"][form="${form.id}"]`) ||
      form.querySelector('[type="submit"]');
    if (submit && !submit.disabled && submit.getAttribute('aria-disabled') !== 'true') return submit;
  }
  return null;
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-pa-add-to-cart]');
  if (!button || button.getAttribute('aria-busy') === 'true') return;
  event.preventDefault();

  const drawer = button.closest('pickup-availability-drawer');
  const errorBox = drawer && drawer.querySelector('[data-pa-cart-error]');
  if (errorBox) errorBox.hidden = true;

  const variantId = button.dataset.variantId;
  const themeSubmit = paFindThemeSubmit(variantId);
  if (themeSubmit) {
    if (drawer) drawer.hide();
    themeSubmit.click();
    return;
  }

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  button.setAttribute('aria-busy', 'true');
  button.disabled = true;

  fetch(`${root}cart/add.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
  })
    .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
    .then(({ ok, data }) => {
      if (!ok) throw new Error((data && (data.description || data.message)) || 'Error');
      window.location.href = `${root}cart`;
    })
    .catch((error) => {
      console.warn('[pickup-availability] No se pudo anadir al carrito:', error.message);
      button.removeAttribute('aria-busy');
      button.disabled = false;
      if (errorBox) {
        errorBox.textContent = button.dataset.error;
        errorBox.hidden = false;
      }
    });
});

// Filas "Disponibilidad en tienda" colocadas en el acordeon (bloque Liquid personalizado).
// Se muestran solo cuando existe el panel para la variante actual.
function paToggleTriggers(visible) {
  document.querySelectorAll('[data-pa-open-drawer]').forEach((trigger) => {
    trigger.hidden = !visible;
  });
}

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-pa-open-drawer]');
  if (!trigger) return;
  const drawer = document.querySelector('pickup-availability-drawer');
  if (!drawer) return;
  event.preventDefault();
  drawer.show(trigger.querySelector('button') || trigger);
});

if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();

        if (!this.hasAttribute('available')) return;

        this.errorHtml = this.querySelector('template').content.firstElementChild.cloneNode(true);
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        this.fetchAvailability(this.dataset.variantId);
      }

      fetchAvailability(variantId) {
        let rootUrl = this.dataset.rootUrl;
        if (!rootUrl.endsWith('/')) {
          rootUrl = rootUrl + '/';
        }
        const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

        fetch(variantSectionUrl)
          .then((response) => response.text())
          .then((text) => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            this.renderPreview(sectionInnerHTML, variantId);
          })
          .catch((e) => {
            const button = this.querySelector('button');
            if (button) button.removeEventListener('click', this.onClickRefreshList);
            this.renderError();
          });
      }

      onClickRefreshList(evt) {
        this.fetchAvailability(this.dataset.variantId);
      }

      renderError() {
        paToggleTriggers(false);
        this.innerHTML = '';
        this.appendChild(this.errorHtml);

        this.querySelector('button').addEventListener('click', this.onClickRefreshList);
      }

      renderPreview(sectionInnerHTML, variantId) {
        const drawer = document.querySelector('pickup-availability-drawer');
        if (drawer) drawer.remove();
        if (!sectionInnerHTML.querySelector('pickup-availability-preview')) {
          this.innerHTML = '';
          this.removeAttribute('available');
          paToggleTriggers(false);
          return;
        }

        this.innerHTML = sectionInnerHTML.querySelector('pickup-availability-preview').outerHTML;
        this.setAttribute('available', '');

        document.body.appendChild(sectionInnerHTML.querySelector('pickup-availability-drawer'));
        const colorClassesToApply = (this.dataset.productPageColorScheme || '').split(' ').filter(Boolean);
        colorClassesToApply.forEach((colorClass) => {
          document.querySelector('pickup-availability-drawer').classList.add(colorClass);
        });

        const button = this.querySelector('button');
        if (button)
          button.addEventListener('click', (evt) => {
            document.querySelector('pickup-availability-drawer').show(evt.target);
          });

        paToggleTriggers(true);
        this.updateStockLevels(variantId || this.dataset.variantId);
      }

      updateStockLevels(variantId) {
        const preview = this.querySelector('pickup-availability-preview');
        if (!preview || !variantId) return;

        const config = preview.dataset;
        if (!config.paToken || !config.paEndpoint) return;

        // Evita que una respuesta lenta de una talla anterior pise a la actual
        const requestId = String(variantId);
        this.stockRequestId = requestId;

        paFetchStockLevels(config, variantId)
          .then((levels) => {
            if (this.stockRequestId !== requestId) return;
            const currentPreview = this.querySelector('pickup-availability-preview');
            const currentDrawer = document.querySelector('pickup-availability-drawer');
            if (!currentPreview || !currentDrawer) return;
            paApplyStockLevels(currentPreview, currentDrawer, levels);
          })
          .catch((error) => {
            console.warn('[pickup-availability] No se pudo leer el inventario por tienda:', error.message);
          });
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define(
    'pickup-availability-drawer',
    class PickupAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();

        this.onBodyClick = this.handleBodyClick.bind(this);

        this.querySelector('button').addEventListener('click', () => {
          this.hide();
        });

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
      }

      handleBodyClick(evt) {
        const target = evt.target;
        if (
          target != this &&
          !target.closest('pickup-availability-drawer') &&
          !target.closest('[data-pa-open-drawer]') &&
          target.id != 'ShowPickupAvailabilityDrawer'
        ) {
          this.hide();
        }
      }

      hide() {
        this.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClick);
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.focusElement);
      }

      show(focusElement) {
        this.focusElement = focusElement;
        this.setAttribute('open', '');
        document.body.addEventListener('click', this.onBodyClick);
        document.body.classList.add('overflow-hidden');
        trapFocus(this);
      }
    }
  );
}
